import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, Input } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {
  NgbDate,
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDatepickerModule,
  NgbPopoverModule
} from '@ng-bootstrap/ng-bootstrap';
import { IMaskModule } from 'angular-imask';
import moment, { Moment } from 'moment';
import { ISO_8601 } from 'moment/moment';
import { MuiButtonModule } from '../../../button';
import { MuiIconModule } from '../../../icon';
import { MuiPresenceDirective } from '../../../utility/presence/mui-presence.directive';
import { MuiFormField } from '../../form-field/mui-form-field.component';
import { MuiInputNativeDirective } from '../../input/mui-input-native.directive';
import { MuiDatePickerBase } from '../mui-date-picker-base';
import { ISO8601StringParserFormatter } from '../providers/iso8601-string-parser-formatter';
import { MomentNgbDateAdapter } from '../providers/moment-ngb-date-adapter';
import { MUI_DATEPICKER_I18N } from '../providers/mui-datepicker-i18n';
import { ISO8601String, MuiDatepickerV2Format } from '../supported-formats';
import { createMaskOptions, momentToNgbDate, ngbDateToMoment } from '../utility';


@Component({
  selector: 'mui-datepicker-v2, mui-date-picker',
  templateUrl: './mui-datepicker-v2.component.html',
  styleUrls: [
    './mui-datepicker-v2.component.scss',
    '../../datepicker/ngb-datepicker.overrides.scss'
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MuiPresenceDirective,
    MuiButtonModule,
    MuiIconModule,
    MuiInputNativeDirective,
    MuiFormField,
    IMaskModule,
    NgbDatepickerModule,
    NgbPopoverModule
  ],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MuiDatepickerV2), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => MuiDatepickerV2), multi: true },
    { provide: NgbDatepickerI18n, useFactory: () => inject(MUI_DATEPICKER_I18N) },
    { provide: NgbDateAdapter, useClass: MomentNgbDateAdapter },
    { provide: NgbDateParserFormatter, useClass: ISO8601StringParserFormatter }
  ]
})
export class MuiDatepickerV2 extends MuiDatePickerBase<ISO8601String> implements ControlValueAccessor, Validator {

  private static nextUniqueId = 0;

  @Input()
  id = `mui-datepicker-v2-${MuiDatepickerV2.nextUniqueId++}`;

  @Input()
  set useMask(value: boolean) {
    this._useMask = coerceBooleanProperty(value);
  }

  get useMask(): boolean {
    return this._useMask && !!this.format;
  }

  protected _model: Moment | null = null;
  private _useMask = false;

  writeValue(value: ISO8601String | null): void {
    if (!value) {
      this._model = null;
      this.updateHTMLInputValue(null);
      return;
    }

    // Try to parse the control's value to a valid date object
    const date = moment(value);
    if (!date.isValid()) {
      this._model = null;
      this.updateHTMLInputValue(null);
      return;
    }

    // Update the HTML input value with the formatted date
    this._model = date;
    this.updateHTMLInputValue(date);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      // We only want to make sure that the control's value is a valid date string if it is defined
      return null;
    }

    // Validate if the form control's value is a valid ISO_8601 date string
    const date = moment(control.value, ISO_8601, true);
    return date.isValid() ? null : { invalidDate: control.value };
  }

  protected getNavigationDate(): Moment | null {
    return this._model;
  }

  protected resetModel(): void {
    this._model = null;
    this.onChange(null);
  }

  protected setupMaskOptions(): void {
    if (!this.useMask) {
      return;
    }

    const placeholderChars = this._datepickerI18n.getPlaceholderChars();
    this._maskOptions = createMaskOptions({
      pattern: this.format,
      min: this.minDate,
      max: this.maxDate,
      placeholderChars
    });
  }

  protected onInputBlur(event: FocusEvent): void {
    if (this._model && this._model.isValid()) {
      this.updateHTMLInputValue(this._model);
    }

    super.onInputBlur(event);
  }

  protected onInputChange(event: Event): void {
    super.onInputChange(event, this.useMask);
  }

  protected updateModel(value: string, strict: boolean): void {
    const date = this.parseDate(value, strict);

    if (date.isSame(this._model, 'date')) {
      return;
    }

    this.onTouched();

    if (!date || !date.isValid()) {
      this._model = null;
      this._datepicker?.focusDate(null);
      this.onChange(value);
      return;
    }

    this._model = date;
    this._datepicker?.focusDate(momentToNgbDate(date));
    this.onChange(this._model?.format('YYYY-MM-DD'));
  }

  protected onDateSelect(value: NgbDate): void {
    this.onTouched();
    const date = ngbDateToMoment(value);
    this._model = date;
    this.updateHTMLInputValue(date);
    this.onChange(this._model.format('YYYY-MM-DD'));
  }

  protected isSelectedDate(date: NgbDate): boolean {
    return this._model ? date.equals(momentToNgbDate(moment(this._model))) : false;
  }

  protected getParserFormat(): MuiDatepickerV2Format | MuiDatepickerV2Format[] {
    return this.useMask ? this.format : [
      ...new Set([
        this.format,
        'YYYY-MM-DD',
        'DD.MM.YYYY',
        'MM/DD/YYYY'
      ])
    ] as MuiDatepickerV2Format[];
  }

  protected updateHTMLInputValue(date: Moment | null): void {
    if (!this._inputElement) {
      return;
    }
    this._inputElement.nativeElement.value = date ? date.format(this.format ?? 'L') : '';
    this._mask?.maskRef?.updateValue();
  }

  protected getLocalizedFormatPlaceholder(): string {
    const placeholderChars = this._datepickerI18n.getPlaceholderChars();
    return this.format?.replace(/Y/g, placeholderChars.year)
      .replace(/M/g, placeholderChars.month)
      .replace(/D/g, placeholderChars.day) ?? '';
  }
}
